import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowDownTrayIcon,
  BanknotesIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { exportToCsv } from '../../utils/registryExport'
import { useAuth } from '../../contexts/AuthContext'

import { formatSydneyDate, formatSydneyDateTime } from '../../utils/sydneyDateTime'
const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'cheque', label: 'Cheque monitoring' },
  { id: 'large', label: 'Large EFT / cheques' },
  { id: 'repeat', label: 'Repeat payees' },
  { id: 'highrisk', label: 'High-risk added' },
  { id: 'pep', label: 'PEP detections' },
  { id: 'austrac', label: 'AUSTRAC log' },
]

function defaultDateRange() {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  const fmt = (d) => d.toISOString().slice(0, 10)
  return { dateFrom: fmt(from), dateTo: fmt(to) }
}

function formatAud(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n)
    ? n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })
    : '—'
}

function formatAud2(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : formatSydneyDate(d)
}

function formatDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime())
    ? '—'
    : formatSydneyDateTime(d)
}

function StatCard({ icon: Icon, label, value, color = '#0A5169', bg = '#e8f2f6' }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex items-center gap-3 min-w-[140px]">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: bg, color }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500 truncate">{label}</div>
        <div className="text-xl font-bold tabular-nums" style={{ color }}>{value ?? '—'}</div>
      </div>
    </div>
  )
}

function metricCell(metric, column) {
  const value = metric.values?.[column]
  if (metric.money) return formatAud2(value)
  return Number(value) || 0
}

function ChequeOversightView({ report }) {
  const columns = report?.columns || []
  const players = report?.players || []
  if (!columns.length && !players.length) {
    return <EmptyState message="No venues or cheque payouts in this period." />
  }
  return (
    <div className="space-y-8 bg-white rounded-lg border border-gray-200 px-4 py-5">
      <p className="text-xs text-gray-500">
        Cheque payouts in the selected dates. Transaction amount is the cheque plus the cash paid on that payout. A cheque count of {report?.chequeCountMark || 3} or more is marked. The amount filter on this page does not apply here.
      </p>
      <section>
        <h2 className="text-base font-bold text-gray-900 mb-3">Group Monthly Cheque Oversight</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-900">
            <thead>
              <tr className="border-b-2 border-gray-900">
                <th className="px-2 py-1 text-left font-bold">Metrics</th>
                {columns.map((col) => (
                  <th key={col} className="px-2 py-1 text-right font-bold whitespace-nowrap">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(report?.metrics || []).map((metric) => (
                <tr
                  key={metric.key}
                  className={metric.emphasize ? 'border-t border-gray-300 font-bold' : 'border-b border-gray-200'}
                >
                  <td className="px-2 py-1.5">{metric.label}</td>
                  {columns.map((col) => (
                    <td key={col} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                      {metricCell(metric, col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section>
        <h2 className="text-base font-bold text-gray-900 mb-3">Group Monthly Player Cheque Summary</h2>
        {players.length === 0 ? (
          <EmptyState message="No cheque payouts in this period." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-900">
              <thead>
                <tr className="border-b-2 border-gray-900">
                  <th className="px-2 py-1 text-left font-bold">Players</th>
                  <th className="px-2 py-1 text-left font-bold">Site</th>
                  <th className="px-2 py-1 text-right font-bold">Cheque Count</th>
                  <th className="px-2 py-1 text-right font-bold">TransactionAmount</th>
                  <th className="px-2 py-1 text-right font-bold">ChequeAmount</th>
                </tr>
              </thead>
              <tbody>
                {players.map((row) => (
                  <tr key={`${row.player}-${row.site}`} className="border-b border-gray-100">
                    <td className="px-2 py-1 uppercase">{row.player}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{row.site}</td>
                    <td className="px-2 py-1 text-right">
                      <span className={row.marked ? 'inline-block min-w-[1.75rem] bg-red-600 px-1.5 py-0.5 text-center font-bold text-white' : 'tabular-nums'}>
                        {row.chequeCount}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">{formatAud2(row.transactionAmount)}</td>
                    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">{formatAud2(row.chequeAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

function EmptyState({ message }) {
  return (
    <div className="py-12 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-lg bg-gray-50">
      {message}
    </div>
  )
}

export default function CompliancePeriodReports() {
  const { can } = useAuth()
  const canCreate = can('compliance-safe/period-reports', 'create')
  const canEdit = can('compliance-safe/period-reports', 'edit')
  const canExport = can('compliance-safe/period-reports', 'export')
    || can('compliance-safe/period-reports', 'view')

  const defaults = useMemo(() => defaultDateRange(), [])
  const [dateFrom, setDateFrom] = useState(defaults.dateFrom)
  const [dateTo, setDateTo] = useState(defaults.dateTo)
  const [minAmount, setMinAmount] = useState('5000')
  const [tab, setTab] = useState('summary')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [chequeReport, setChequeReport] = useState(null)
  const [largeRows, setLargeRows] = useState([])
  const [repeatRows, setRepeatRows] = useState([])
  const [highRiskRows, setHighRiskRows] = useState([])
  const [pepRows, setPepRows] = useState([])
  const [corrRows, setCorrRows] = useState([])
  const [showCorrForm, setShowCorrForm] = useState(false)
  const [corrForm, setCorrForm] = useState({
    correspondence_date: defaults.dateTo,
    direction: 'outbound',
    regulator: 'AUSTRAC',
    subject: '',
    reference_number: '',
    notes: '',
  })
  const [savingCorr, setSavingCorr] = useState(false)

  const qs = useMemo(() => {
    const p = new URLSearchParams()
    if (dateFrom) p.set('dateFrom', dateFrom)
    if (dateTo) p.set('dateTo', dateTo)
    if (minAmount) p.set('minAmount', minAmount)
    return p.toString()
  }, [dateFrom, dateTo, minAmount])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const base = `/api/compliance/period-listings`
      const chequeQs = new URLSearchParams()
      if (dateFrom) chequeQs.set('dateFrom', dateFrom)
      if (dateTo) chequeQs.set('dateTo', dateTo)
      const [sumRes, chequeRes, largeRes, repeatRes, highRes, pepRes, corrRes] = await Promise.all([
        authFetch(getLocalApiUrl(`${base}/summary?${qs}`)),
        authFetch(getLocalApiUrl(`${base}/cheque-oversight?${chequeQs}`)),
        authFetch(getLocalApiUrl(`${base}/large-non-cash-payouts?${qs}&limit=200`)),
        authFetch(getLocalApiUrl(`${base}/repeat-large-payees?${qs}`)),
        authFetch(getLocalApiUrl(`${base}/high-risk-added?${qs}&limit=200`)),
        authFetch(getLocalApiUrl(`${base}/pep-detections?${qs}&limit=200`)),
        authFetch(getLocalApiUrl(`${base}/correspondence?${qs}`)),
      ])
      const [sumJ, chequeJ, largeJ, repeatJ, highJ, pepJ, corrJ] = await Promise.all([
        sumRes.json().catch(() => ({})),
        chequeRes.json().catch(() => ({})),
        largeRes.json().catch(() => ({})),
        repeatRes.json().catch(() => ({})),
        highRes.json().catch(() => ({})),
        pepRes.json().catch(() => ({})),
        corrRes.json().catch(() => ({})),
      ])
      if (!sumJ.success && sumRes.status === 403) {
        throw new Error('You do not have permission to view Compliance reports. Ask an admin to grant compliance-safe/period-reports:view.')
      }
      if (sumJ.success) setSummary(sumJ.data)
      else setSummary(null)
      setChequeReport(chequeJ.success ? chequeJ.data : null)
      setLargeRows(largeJ.success ? largeJ.data || [] : [])
      setRepeatRows(repeatJ.success ? repeatJ.data || [] : [])
      setHighRiskRows(highJ.success ? highJ.data || [] : [])
      setPepRows(pepJ.success ? pepJ.data || [] : [])
      setCorrRows(corrJ.success ? corrJ.data || [] : [])
      if (!largeJ.success && !sumJ.success) {
        throw new Error(sumJ.error || largeJ.error || 'Failed to load period listings')
      }
    } catch (e) {
      setError(e.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [qs])

  useEffect(() => { load() }, [load])

  const exportCurrent = () => {
    const stamp = `${dateFrom || 'start'}_${dateTo || 'end'}`
    if (tab === 'cheque') {
      exportToCsv(
        chequeReport?.players || [],
        [
          { header: 'Players', key: 'player' },
          { header: 'Site', key: 'site' },
          { header: 'Cheque Count', key: 'chequeCount' },
          { header: 'TransactionAmount', key: 'transactionAmount' },
          { header: 'ChequeAmount', key: 'chequeAmount' },
        ],
        `cheque-player-summary-${stamp}.csv`
      )
    } else if (tab === 'large') {
      exportToCsv(
        largeRows,
        [
          { header: 'Date', accessor: (r) => formatDateTime(r.timestamp || r.created_at) },
          { header: 'Member ID', key: 'member_id' },
          { header: 'Name', key: 'member_name' },
          { header: 'Venue', key: 'venue' },
          { header: 'Amount', key: 'amount' },
          { header: 'EFT', key: 'eft_amount' },
          { header: 'Cheque', key: 'cheque_amount' },
          { header: 'Method', key: 'payment_method' },
          { header: 'Tier', key: 'payout_tier' },
        ],
        `large-non-cash-payouts-${stamp}.csv`
      )
    } else if (tab === 'repeat') {
      exportToCsv(
        repeatRows,
        [
          { header: 'Member ID', key: 'member_id' },
          { header: 'Name', key: 'member_name' },
          { header: 'Count', key: 'payout_count' },
          { header: 'Total', key: 'total_amount' },
          { header: 'Max', key: 'max_amount' },
          { header: 'Total EFT', key: 'total_eft' },
          { header: 'Total cheque', key: 'total_cheque' },
        ],
        `repeat-large-payees-${stamp}.csv`
      )
    } else if (tab === 'highrisk') {
      exportToCsv(
        highRiskRows,
        [
          { header: 'When', accessor: (r) => formatDateTime(r.set_at) },
          { header: 'Member ID', key: 'member_id' },
          { header: 'Name', key: 'full_name' },
          { header: 'From tier', key: 'from_tier' },
          { header: 'To tier', key: 'to_tier' },
          { header: 'Risk score', key: 'risk_score' },
          { header: 'Source', key: 'source' },
          { header: 'Reason', key: 'reason' },
        ],
        `high-risk-added-${stamp}.csv`
      )
    } else if (tab === 'pep') {
      exportToCsv(
        pepRows,
        [
          { header: 'When', accessor: (r) => formatDateTime(r.pep_status_changed_at || r.created_at) },
          { header: 'Member ID', key: 'member_id' },
          { header: 'Name', key: 'full_name' },
          { header: 'Status', key: 'status' },
          { header: 'Sanctions', key: 'sanctions_match' },
          { header: 'Category', key: 'aml_derived_category' },
          { header: 'Performed by', key: 'performed_by' },
        ],
        `pep-detections-${stamp}.csv`
      )
    } else if (tab === 'austrac') {
      exportToCsv(
        corrRows,
        [
          { header: 'Date', accessor: (r) => formatDate(r.correspondence_date) },
          { header: 'Direction', key: 'direction' },
          { header: 'Regulator', key: 'regulator' },
          { header: 'Subject', key: 'subject' },
          { header: 'Reference', key: 'reference_number' },
          { header: 'Notes', key: 'notes' },
        ],
        `austrac-correspondence-${stamp}.csv`
      )
    }
  }

  const saveCorrespondence = async (e) => {
    e.preventDefault()
    if (!corrForm.subject.trim()) return
    setSavingCorr(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/compliance/period-listings/correspondence'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corrForm),
      })
      const j = await res.json().catch(() => ({}))
      if (!j.success) throw new Error(j.error || 'Save failed')
      setShowCorrForm(false)
      setCorrForm((f) => ({ ...f, subject: '', reference_number: '', notes: '' }))
      await load()
      setTab('austrac')
    } catch (err) {
      setError(err.message || 'Failed to save correspondence')
    } finally {
      setSavingCorr(false)
    }
  }

  const deleteCorrespondence = async (id) => {
    if (!canEdit || !window.confirm('Delete this correspondence entry?')) return
    try {
      const res = await authFetch(getLocalApiUrl(`/api/compliance/period-listings/correspondence/${id}`), {
        method: 'DELETE',
      })
      const j = await res.json().catch(() => ({}))
      if (!j.success) throw new Error(j.error || 'Delete failed')
      await load()
    } catch (err) {
      setError(err.message || 'Failed to delete')
    }
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-[1400px]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <DocumentTextIcon className="w-6 h-6 text-psg-teal" />
            Compliance reports
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Period listings for AML/CTF board packs (large non-cash payouts, repeat payees, high-risk / ECDD escalations, PEP hits, AUSTRAC correspondence). CSV export now; narrative Word/PDF pack next.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canExport && tab !== 'summary' && (
            <Button type="button" variant="outline" size="sm" onClick={exportCurrent} disabled={loading}>
              <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
              Export CSV
            </Button>
          )}
          <Button type="button" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1">Min amount ($)</span>
          <input
            type="number"
            min="0"
            step="100"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-28"
          />
        </label>
        <p className="text-xs text-gray-400 pb-2">
          Also available on{' '}
          <Link className="text-psg-teal underline" to="/payout-safe/monitoring">
            Payout Process List
          </Link>
          {' '}(Non-cash + $5,000+).
        </p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? 'border-psg-teal text-psg-teal'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.id === 'large' && summary != null && (
              <span className="ml-1.5 text-xs tabular-nums text-gray-400">({summary.largeNonCashPayouts})</span>
            )}
            {t.id === 'repeat' && summary != null && (
              <span className="ml-1.5 text-xs tabular-nums text-gray-400">({summary.repeatLargePayees})</span>
            )}
            {t.id === 'highrisk' && summary != null && (
              <span className="ml-1.5 text-xs tabular-nums text-gray-400">({summary.highRiskAdded})</span>
            )}
            {t.id === 'pep' && summary != null && (
              <span className="ml-1.5 text-xs tabular-nums text-gray-400">({summary.pepDetections})</span>
            )}
            {t.id === 'austrac' && summary != null && (
              <span className="ml-1.5 text-xs tabular-nums text-gray-400">({summary.correspondence})</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-500 py-6">Loading period listings…</div>
      )}

      {!loading && tab === 'summary' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <StatCard icon={BanknotesIcon} label="Large non-cash" value={summary?.largeNonCashPayouts ?? 0} />
            <StatCard icon={UserGroupIcon} label="Repeat payees" value={summary?.repeatLargePayees ?? 0} color="#92400e" bg="#fef3c7" />
            <StatCard icon={ExclamationTriangleIcon} label="High-risk added" value={summary?.highRiskAdded ?? 0} color="#991b1b" bg="#fee2e2" />
            <StatCard icon={ShieldExclamationIcon} label="PEP detections" value={summary?.pepDetections ?? 0} color="#5b21b6" bg="#ede9fe" />
            <StatCard icon={DocumentTextIcon} label="AUSTRAC log" value={summary?.correspondence ?? 0} />
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600 space-y-2">
            <p className="font-medium text-gray-800">Report pack (next)</p>
            <p>
              These counts feed Geoff’s AML/CTF period template. SMR/TTR/ECDD/training/risk register remain on their existing registries; this page owns the gap listings and AUSTRAC correspondence log.
            </p>
            <ul className="list-disc pl-5 space-y-1 text-gray-500">
              <li>Word/PDF narrative export with declaration block</li>
              <li>Saved report instances per period</li>
              <li>Client-specific section packs beyond WTLC</li>
            </ul>
          </div>
        </div>
      )}

      {!loading && tab === 'cheque' && (
        <ChequeOversightView report={chequeReport} />
      )}

      {!loading && tab === 'large' && (
        largeRows.length === 0 ? (
          <EmptyState message="No large EFT/cheque payouts in this period." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Patron</th>
                  <th className="px-3 py-2">Venue</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">EFT</th>
                  <th className="px-3 py-2 text-right">Cheque</th>
                  <th className="px-3 py-2">Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {largeRows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(r.timestamp || r.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.member_name || '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.member_id || r.master_profile_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2">{r.venue || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.amount)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.eft_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.cheque_amount)}</td>
                    <td className="px-3 py-2">{r.payment_method || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'repeat' && (
        repeatRows.length === 0 ? (
          <EmptyState message="No patrons with 2+ large non-cash payouts in this period." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Patron</th>
                  <th className="px-3 py-2 text-right">Count</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Max</th>
                  <th className="px-3 py-2 text-right">EFT total</th>
                  <th className="px-3 py-2">First → last</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {repeatRows.map((r) => (
                  <tr key={r.patron_key} className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.member_name || '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.member_id || r.master_profile_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">{r.payout_count}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.total_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.max_amount)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatAud(r.total_eft)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {formatDate(r.first_payout_at)} → {formatDate(r.last_payout_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'highrisk' && (
        highRiskRows.length === 0 ? (
          <EmptyState message="No enhanced-CDD / high-risk escalations recorded in this period." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Patron</th>
                  <th className="px-3 py-2">Tier change</th>
                  <th className="px-3 py-2">Risk</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {highRiskRows.map((r, i) => (
                  <tr key={r.id || `${r.member_id}-${i}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(r.set_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.full_name || '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.member_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-gray-500">{r.from_tier || '—'}</span>
                      {' → '}
                      <span className="font-semibold text-red-700">{r.to_tier || '—'}</span>
                    </td>
                    <td className="px-3 py-2 font-mono">{r.risk_score ?? '—'}</td>
                    <td className="px-3 py-2">{r.source || '—'}</td>
                    <td className="px-3 py-2 max-w-xs truncate" title={r.reason || ''}>{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'pep' && (
        pepRows.length === 0 ? (
          <EmptyState message="No PEP-positive screening results in this period." />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Patron</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sanctions</th>
                  <th className="px-3 py-2">Category</th>
                  <th className="px-3 py-2">By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pepRows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(r.pep_status_changed_at || r.created_at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.full_name || '—'}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.member_id || '—'}</div>
                    </td>
                    <td className="px-3 py-2">{r.status || '—'}</td>
                    <td className="px-3 py-2">{r.sanctions_match ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2">{r.aml_derived_category || '—'}</td>
                    <td className="px-3 py-2">{r.performed_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'austrac' && (
        <div className="space-y-3">
          <div className="flex justify-end">
            {canCreate && (
              <Button type="button" size="sm" onClick={() => setShowCorrForm((v) => !v)}>
                <PlusIcon className="w-4 h-4 mr-1.5" />
                Add correspondence
              </Button>
            )}
          </div>
          {showCorrForm && (
            <form onSubmit={saveCorrespondence} className="rounded-lg border border-gray-200 bg-white p-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Date</span>
                <input
                  type="date"
                  required
                  value={corrForm.correspondence_date}
                  onChange={(e) => setCorrForm({ ...corrForm, correspondence_date: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Direction</span>
                <select
                  value={corrForm.direction}
                  onChange={(e) => setCorrForm({ ...corrForm, direction: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                >
                  <option value="outbound">Outbound</option>
                  <option value="inbound">Inbound</option>
                  <option value="meeting">Meeting</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="block text-xs text-gray-500 mb-1">Subject</span>
                <input
                  required
                  value={corrForm.subject}
                  onChange={(e) => setCorrForm({ ...corrForm, subject: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                  placeholder="e.g. Response to AUSTRAC information request"
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Reference</span>
                <input
                  value={corrForm.reference_number}
                  onChange={(e) => setCorrForm({ ...corrForm, reference_number: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1">Regulator</span>
                <input
                  value={corrForm.regulator}
                  onChange={(e) => setCorrForm({ ...corrForm, regulator: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5"
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="block text-xs text-gray-500 mb-1">Notes</span>
                <textarea
                  value={corrForm.notes}
                  onChange={(e) => setCorrForm({ ...corrForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 min-h-[80px]"
                />
              </label>
              <div className="sm:col-span-2 flex gap-2">
                <Button type="submit" size="sm" disabled={savingCorr}>{savingCorr ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowCorrForm(false)}>Cancel</Button>
              </div>
            </form>
          )}
          {corrRows.length === 0 ? (
            <EmptyState message="No AUSTRAC / regulator correspondence logged for this period." />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">Direction</th>
                    <th className="px-3 py-2">Subject</th>
                    <th className="px-3 py-2">Reference</th>
                    <th className="px-3 py-2">Notes</th>
                    {canEdit && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {corrRows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.correspondence_date)}</td>
                      <td className="px-3 py-2 capitalize">{r.direction}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{r.subject}</td>
                      <td className="px-3 py-2 font-mono text-xs">{r.reference_number || '—'}</td>
                      <td className="px-3 py-2 max-w-sm truncate text-gray-600" title={r.notes || ''}>{r.notes || '—'}</td>
                      {canEdit && (
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() => deleteCorrespondence(r.id)}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
